import React from "react";
import { useFormContext, useFieldArray, Controller } from "react-hook-form";
import { 
  Input, 
  Textarea, 
  Switch, 
  Label, 
  Button, 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle 
} from "@wove/web-ui";
import { PlusIcon, TrashIcon, ChevronUpIcon, ChevronDownIcon } from "lucide-react";
import { createTemplateFromSchema } from "../utils/schemaUtils";

// Component for rendering a single field (both view and edit modes)
interface DocumentFieldProps {
  label?: string;
  path: string;
  isEditing: boolean;
  schema: any;
  fullWidth?: boolean;
  complexType?: "weight" | "volume" | "dimensions" | "currency";
}

export const DocumentField: React.FC<DocumentFieldProps> = ({ 
  label, 
  path, 
  isEditing, 
  schema, 
  fullWidth = false,
  complexType 
}) => {
  const { control, watch } = useFormContext();
  const value = watch(path);

  if (!isEditing) {
    // View mode
    if (value === undefined || value === null) return null;

    // Format complex objects for display
    let displayValue;
    if (typeof value === "boolean") {
      displayValue = value ? "Yes" : "No";
    } else if ((typeof value === "object" && value !== null) || complexType) {
      // Handle complex types based on complexType prop or object structure
      if (complexType) {
        switch (complexType) {
          case "weight":
          case "volume":
            if (value.value !== undefined && value.unit !== undefined) {
              displayValue = `${value.value} ${value.unit}`;
            } else {
              displayValue = "N/A";
            }
            break;
          case "currency":
            if (value.amount !== undefined && value.currency !== undefined) {
              displayValue = `${value.amount} ${value.currency}`;
            } else if (value.value !== undefined && value.currency !== undefined) {
              displayValue = `${value.value} ${value.currency}`;
            } else {
              displayValue = "N/A";
            }
            break;
          case "dimensions":
            if (
              value.length !== undefined &&
              value.width !== undefined &&
              value.height !== undefined &&
              value.unit !== undefined
            ) {
              displayValue = `${value.length}x${value.width}x${value.height} ${value.unit}`;
            } else {
              displayValue = "N/A";
            }
            break;
          default:
            displayValue = typeof value === "object" ? JSON.stringify(value) : value || "N/A";
        }
      } else if (typeof value === "object") {
        // Handle complex objects like weight, volume, dimensions, currency
        if (value.value !== undefined && value.unit !== undefined) {
          displayValue = `${value.value} ${value.unit}`;
        } else if (value.value !== undefined && value.currency !== undefined) {
          displayValue = `${value.value} ${value.currency}`;
        } else if (value.amount !== undefined && value.currency !== undefined) {
          displayValue = `${value.amount} ${value.currency}`;
        } else if (
          value.length !== undefined &&
          value.width !== undefined &&
          value.height !== undefined &&
          value.unit !== undefined
        ) {
          displayValue = `${value.length}x${value.width}x${value.height} ${value.unit}`;
        } else {
          // For other objects, try to display as JSON or "Complex Object"
          try {
            displayValue = JSON.stringify(value);
          } catch (e) {
            displayValue = "Complex Object";
          }
        }
      } else {
        displayValue = value;
      }
    } else {
      displayValue = value || "N/A";
    }

    return (
      <>
        {label && <div className="text-gray-600">{label}:</div>}
        <div className={fullWidth ? "col-span-2" : ""}>{displayValue}</div>
      </>
    );
  }

  // Edit mode
  return (
    <div className={fullWidth ? "col-span-2" : "col-span-2"}>
      <Controller
        control={control}
        name={path}
        render={({ field }) => {
          // Handle complex types if specified
          if (complexType) {
            // Initialize value as object if it's not already
            const currentValue = field.value || {};
            
            switch (complexType) {
              case "weight":
              case "volume":
                return (
                  <div className="mb-2">
                    {label && <Label className="text-sm font-medium">{label}</Label>}
                    <div className="grid grid-cols-2 gap-2 mt-1">
                      <Input
                        type="number"
                        value={currentValue.value || ""}
                        onChange={(e) => {
                          field.onChange({ ...currentValue, value: parseFloat(e.target.value) || null });
                        }}
                        placeholder="Value"
                      />
                      <Input
                        value={currentValue.unit || ""}
                        onChange={(e) => {
                          field.onChange({ ...currentValue, unit: e.target.value });
                        }}
                        placeholder="Unit"
                      />
                    </div>
                  </div>
                );
              case "currency":
                return (
                  <div className="mb-2">
                    {label && <Label className="text-sm font-medium">{label}</Label>}
                    <div className="grid grid-cols-2 gap-2 mt-1">
                      <Input
                        type="number"
                        value={currentValue.amount || currentValue.value || ""}
                        onChange={(e) => {
                          const numValue = parseFloat(e.target.value) || null;
                          if (currentValue.hasOwnProperty('amount')) {
                            field.onChange({ ...currentValue, amount: numValue });
                          } else {
                            field.onChange({ ...currentValue, value: numValue });
                          }
                        }}
                        placeholder="Amount"
                      />
                      <Input
                        value={currentValue.currency || ""}
                        onChange={(e) => {
                          field.onChange({ ...currentValue, currency: e.target.value });
                        }}
                        placeholder="Currency"
                      />
                    </div>
                  </div>
                );
              case "dimensions":
                return (
                  <div className="mb-2">
                    {label && <Label className="text-sm font-medium">{label}</Label>}
                    <div className="grid grid-cols-4 gap-1 mt-1">
                      <Input
                        type="number"
                        value={currentValue.length || ""}
                        onChange={(e) => {
                          field.onChange({ ...currentValue, length: parseFloat(e.target.value) || null });
                        }}
                        placeholder="L"
                      />
                      <Input
                        type="number"
                        value={currentValue.width || ""}
                        onChange={(e) => {
                          field.onChange({ ...currentValue, width: parseFloat(e.target.value) || null });
                        }}
                        placeholder="W"
                      />
                      <Input
                        type="number"
                        value={currentValue.height || ""}
                        onChange={(e) => {
                          field.onChange({ ...currentValue, height: parseFloat(e.target.value) || null });
                        }}
                        placeholder="H"
                      />
                      <Input
                        value={currentValue.unit || ""}
                        onChange={(e) => {
                          field.onChange({ ...currentValue, unit: e.target.value });
                        }}
                        placeholder="Unit"
                      />
                    </div>
                  </div>
                );
            }
          }
          
          // Handle complex objects (value + unit/currency) - for backward compatibility
          if (
            typeof field.value === "object" &&
            field.value !== null &&
            ((field.value.value !== undefined && field.value.unit !== undefined) ||
              (field.value.value !== undefined && field.value.currency !== undefined) ||
              (field.value.amount !== undefined && field.value.currency !== undefined))
          ) {
            const isValueUnit = field.value.value !== undefined && field.value.unit !== undefined;
            const isValueCurrency = field.value.value !== undefined && field.value.currency !== undefined;
            const isAmountCurrency = field.value.amount !== undefined && field.value.currency !== undefined;

            return (
              <div className="mb-2">
                {label && <Label className="text-sm font-medium">{label}</Label>}
                <div className="grid grid-cols-2 gap-2 mt-1">
                  <Input
                    type="number"
                    value={isAmountCurrency ? field.value.amount || "" : field.value.value || ""}
                    onChange={(e) => {
                      const numValue = parseFloat(e.target.value) || 0;
                      if (isAmountCurrency) {
                        field.onChange({ ...field.value, amount: numValue });
                      } else {
                        field.onChange({ ...field.value, value: numValue });
                      }
                    }}
                    placeholder={isAmountCurrency ? "Amount" : "Value"}
                  />
                  <Input
                    value={isValueUnit ? field.value.unit || "" : field.value.currency || ""}
                    onChange={(e) => {
                      if (isValueUnit) {
                        field.onChange({ ...field.value, unit: e.target.value });
                      } else {
                        field.onChange({ ...field.value, currency: e.target.value });
                      }
                    }}
                    placeholder={isValueUnit ? "Unit" : "Currency"}
                  />
                </div>
              </div>
            );
          }

          // Handle date fields
          if (schema?.type === "string" && schema.format === "date") {
            return (
              <div className="mb-2">
                {label && <Label className="text-sm font-medium">{label}</Label>}
                <Input
                  type="date"
                  {...field}
                  value={field.value || ""}
                  onChange={(e) => field.onChange(e.target.value)}
                  className="mt-1"
                  required={false}
                />
              </div>
            );
          }

          // Handle address fields (use textarea)
          if (schema?.type === "string" && (schema.description?.includes("address") || fullWidth)) {
            return (
              <div className="mb-2">
                {label && <Label className="text-sm font-medium">{label}</Label>}
                <Textarea
                  {...field}
                  value={field.value || ""}
                  onChange={(e) => field.onChange(e.target.value)}
                  className="mt-1"
                  rows={2}
                  required={false}
                />
              </div>
            );
          }

          // Handle boolean fields
          if (schema?.type === "boolean") {
            return (
              <div className="mb-2">
                {label && <Label className="text-sm font-medium">{label}</Label>}
                <div className="flex items-center space-x-2 mt-1">
                  <Switch checked={field.value || false} onCheckedChange={field.onChange} required={false} />
                  <Label>{field.value ? "Yes" : "No"}</Label>
                </div>
              </div>
            );
          }

          // Handle number fields
          if (schema?.type === "number") {
            return (
              <div className="mb-2">
                {label && <Label className="text-sm font-medium">{label}</Label>}
                <Input
                  type="number"
                  {...field}
                  value={field.value || ""}
                  onChange={(e) => field.onChange(parseFloat(e.target.value) || null)}
                  className="mt-1"
                  required={false}
                />
              </div>
            );
          }

          // Default to text input
          return (
            <div className="mb-2">
              {label && <Label className="text-sm font-medium">{label}</Label>}
              <Input
                {...field}
                value={field.value || ""}
                onChange={(e) => field.onChange(e.target.value)}
                className="mt-1"
                required={false}
              />
            </div>
          );
        }}
      />
    </div>
  );
};

// Component for rendering object fields
interface DocumentObjectFieldProps {
  label?: string;
  path: string;
  isEditing: boolean;
  schema: any;
  fields?: Array<{
    label: string;
    path: string;
  }>;
  optional?: boolean;
}

export const DocumentObjectField: React.FC<DocumentObjectFieldProps> = ({
  label,
  path,
  isEditing,
  schema,
  fields,
  optional = false,
}) => {
  const { watch } = useFormContext();
  const value = watch(path);

  // Skip if optional and empty
  if (optional && (!value || Object.keys(value || {}).length === 0)) {
    if (!isEditing) return null;
  }

  return (
    <Card className="shadow-sm">
      <CardHeader className="py-2 px-4">
        <CardTitle className="text-md">{label}</CardTitle>
      </CardHeader>
      <CardContent className="py-2 px-4 text-sm">
        {isEditing ? (
          <div className="space-y-2">
            {fields?.map((field) => {
              console.log("FIELD", field, schema);
              return (
                <DocumentField
                  key={field.path}
                  label={field.label}
                  path={`${path}.${field.path}`}
                  isEditing={isEditing}
                  schema={schema?.properties?.[field.path]}
                />
              );
            })}
          </div>
        ) : (
          <div className="space-y-1">
            {fields?.map((field) => {
              const fieldValue = value?.[field.path];
              if (fieldValue === undefined || fieldValue === null || fieldValue === "") return null;

              // Format the field value for display
              let displayValue;
              if (typeof fieldValue === "boolean") {
                displayValue = fieldValue ? "Yes" : "No";
              } else if (typeof fieldValue === "object" && fieldValue !== null) {
                // Handle complex objects like weight, volume, value with currency
                if (fieldValue.value !== undefined && fieldValue.unit !== undefined) {
                  displayValue = `${fieldValue.value} ${fieldValue.unit}`;
                } else if (fieldValue.value !== undefined && fieldValue.currency !== undefined) {
                  displayValue = `${fieldValue.value} ${fieldValue.currency}`;
                } else if (fieldValue.amount !== undefined && fieldValue.currency !== undefined) {
                  displayValue = `${fieldValue.amount} ${fieldValue.currency}`;
                } else {
                  // For other objects, try to display as JSON or "Complex Object"
                  try {
                    displayValue = JSON.stringify(fieldValue);
                  } catch (e) {
                    displayValue = "Complex Object";
                  }
                }
              } else {
                displayValue = String(fieldValue);
              }

              return (
                <div key={field.path} className="flex">
                  <span className="text-gray-600 font-medium">{field.label}:</span>
                  <span className="ml-2">{displayValue}</span>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Component for rendering array fields
interface DocumentArrayFieldProps {
  label?: string;
  path: string;
  isEditing: boolean;
  schema: any;
  columns?: Array<{
    label: string;
    path: string;
    render?: (value: any) => string;
    complexType?: "weight" | "volume" | "dimensions" | "currency";
  }>;
  optional?: boolean;
  onItemAdded?: (item: any, index: number) => void;
  onItemRemoved?: (item: any, index: number) => void;
  onItemMoved?: (item: any, oldIndex: number, newIndex: number) => void;
}

export const DocumentArrayField: React.FC<DocumentArrayFieldProps> = ({
  label,
  path,
  isEditing,
  schema,
  columns = [],
  onItemAdded,
  onItemRemoved,
  onItemMoved,
}) => {
  const { control, getValues } = useFormContext();
  const { fields, append, remove, move } = useFieldArray({
    control,
    name: path,
  });

  // Custom append function that also calls the tracking callback
  const handleAppend = () => {
    // Create a new empty item based on the schema
    const newItem = createEmptyItem(schema?.items);

    // Append to the form
    append(newItem);

    // Call the tracking callback if provided
    if (onItemAdded) {
      // Get the current array length to determine the new item's index
      const currentValues = getValues(path) || [];
      onItemAdded(newItem, currentValues.length - 1);
    }
  };

  // Custom remove function that also calls the tracking callback
  const handleRemove = (index: number) => {
    // Get the item before removing it
    const itemToRemove = getValues(`${path}.${index}`);

    // Call the tracking callback if provided
    if (onItemRemoved) {
      onItemRemoved(itemToRemove, index);
    }

    // Remove from the form
    remove(index);
  };

  // Custom move function that also calls the tracking callback
  const handleMove = (oldIndex: number, newIndex: number) => {
    // Get the item being moved
    const itemToMove = getValues(`${path}.${oldIndex}`);

    // Call the tracking callback if provided
    if (onItemMoved) {
      onItemMoved(itemToMove, oldIndex, newIndex);
    }

    // Move in the form
    move(oldIndex, newIndex);
  };

  // Format complex values for display
  const formatValue = (item: any, column: any) => {
    if (column.render) {
      return column.render(item[column.path], item);
    }

    const value = item[column.path];

    if (value === undefined || value === null) {
      return "N/A";
    }

    if (typeof value === "boolean") {
      return value ? "Yes" : "No";
    }

    if (typeof value === "object" && column.complexType) {
      // Handle different complex types
      switch (column.complexType) {
        case "weight":
        case "volume":
          if (value.value !== undefined && value.unit !== undefined) {
            return `${value.value} ${value.unit}`;
          }
          break;
        case "currency":
          if (value.amount !== undefined && value.currency !== undefined) {
            return `${value.amount} ${value.currency}`;
          }
          if (value.value !== undefined && value.currency !== undefined) {
            return `${value.value} ${value.currency}`;
          }
          break;
        case "dimensions":
          if (
            value.length !== undefined &&
            value.width !== undefined &&
            value.height !== undefined &&
            value.unit !== undefined
          ) {
            return `${value.length}x${value.width}x${value.height} ${value.unit}`;
          }
          break;
      }
      // Fallback for complex objects
      try {
        return JSON.stringify(value);
      } catch (e) {
        return "Complex Object";
      }
    }

    if (typeof value === "object") {
      // Handle nested objects like weight, volume without complexType specified
      if (value.value !== undefined && value.unit !== undefined) {
        return `${value.value} ${value.unit}`;
      }

      // For other objects, return JSON string
      return JSON.stringify(value);
    }

    return String(value);
  };

  return (
    <Card className="shadow-sm">
      <CardHeader className="py-2 px-4 flex flex-row items-center justify-between">
        <CardTitle className="text-md">{label}</CardTitle>
        {isEditing && (
          <div className="flex justify-end mt-2">
            <Button type="button" variant="outline" size="sm" onClick={handleAppend}>
              <PlusIcon className="h-4 w-4 mr-1" />
              Add Item
            </Button>
          </div>
        )}
      </CardHeader>
      <CardContent className="py-2 px-4">
        {fields.length === 0 ? (
          <p className="text-center text-gray-500 text-sm italic">No items available</p>
        ) : isEditing ? (
          // Edit mode - show form fields for each item
          <div className="space-y-3">
            {fields.map((field, index) => (
              <Card key={field.id} className="mb-2 shadow-sm">
                <CardHeader className="py-1 px-3 flex flex-row items-center justify-between">
                  <CardTitle className="text-sm">Item {index + 1}</CardTitle>
                  <div className="flex items-center space-x-2">
                    <Button type="button" variant="ghost" size="sm" onClick={() => handleRemove(index)}>
                      <TrashIcon className="h-4 w-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleMove(index, index - 1)}
                      disabled={index === 0}
                    >
                      <ChevronUpIcon className="h-4 w-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleMove(index, index + 1)}
                      disabled={index === fields.length - 1}
                    >
                      <ChevronDownIcon className="h-4 w-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="py-2 px-3">
                  <div className="grid grid-cols-1 gap-2">
                    {columns.map((column) => {
                      if (column.complexType) {
                        // Handle complex fields based on type
                        switch (column.complexType) {
                          case "weight":
                          case "volume":
                            return (
                              <div key={column.path} className="mb-2">
                                <Label className="text-sm font-medium">{column.label}</Label>
                                <div className="grid grid-cols-2 gap-2 mt-1">
                                  <Controller
                                    control={control}
                                    name={`${path}.${index}.${column.path}.value`}
                                    render={({ field }) => (
                                      <Input
                                        type="number"
                                        {...field}
                                        value={field.value || ""}
                                        onChange={(e) => field.onChange(parseFloat(e.target.value) || null)}
                                        placeholder="Value"
                                      />
                                    )}
                                  />
                                  <Controller
                                    control={control}
                                    name={`${path}.${index}.${column.path}.unit`}
                                    render={({ field }) => (
                                      <Input
                                        {...field}
                                        value={field.value || ""}
                                        onChange={(e) => field.onChange(e.target.value)}
                                        placeholder="Unit"
                                      />
                                    )}
                                  />
                                </div>
                              </div>
                            );
                          case "currency":
                            return (
                              <div key={column.path} className="mb-2">
                                <Label className="text-sm font-medium">{column.label}</Label>
                                <div className="grid grid-cols-2 gap-2 mt-1">
                                  <Controller
                                    control={control}
                                    name={`${path}.${index}.${column.path}.amount`}
                                    render={({ field }) => (
                                      <Input
                                        type="number"
                                        {...field}
                                        value={field.value || ""}
                                        onChange={(e) => field.onChange(parseFloat(e.target.value) || null)}
                                        placeholder="Amount"
                                      />
                                    )}
                                  />
                                  <Controller
                                    control={control}
                                    name={`${path}.${index}.${column.path}.currency`}
                                    render={({ field }) => (
                                      <Input
                                        {...field}
                                        value={field.value || ""}
                                        onChange={(e) => field.onChange(e.target.value)}
                                        placeholder="Currency"
                                      />
                                    )}
                                  />
                                </div>
                              </div>
                            );
                          case "dimensions":
                            return (
                              <div key={column.path} className="mb-2">
                                <Label className="text-sm font-medium">{column.label}</Label>
                                <div className="grid grid-cols-4 gap-1 mt-1">
                                  <Controller
                                    control={control}
                                    name={`${path}.${index}.${column.path}.length`}
                                    render={({ field }) => (
                                      <Input
                                        type="number"
                                        {...field}
                                        value={field.value || ""}
                                        onChange={(e) => field.onChange(parseFloat(e.target.value) || null)}
                                        placeholder="L"
                                      />
                                    )}
                                  />
                                  <Controller
                                    control={control}
                                    name={`${path}.${index}.${column.path}.width`}
                                    render={({ field }) => (
                                      <Input
                                        type="number"
                                        {...field}
                                        value={field.value || ""}
                                        onChange={(e) => field.onChange(parseFloat(e.target.value) || null)}
                                        placeholder="W"
                                      />
                                    )}
                                  />
                                  <Controller
                                    control={control}
                                    name={`${path}.${index}.${column.path}.height`}
                                    render={({ field }) => (
                                      <Input
                                        type="number"
                                        {...field}
                                        value={field.value || ""}
                                        onChange={(e) => field.onChange(parseFloat(e.target.value) || null)}
                                        placeholder="H"
                                      />
                                    )}
                                  />
                                  <Controller
                                    control={control}
                                    name={`${path}.${index}.${column.path}.unit`}
                                    render={({ field }) => (
                                      <Input
                                        {...field}
                                        value={field.value || ""}
                                        onChange={(e) => field.onChange(e.target.value)}
                                        placeholder="Unit"
                                      />
                                    )}
                                  />
                                </div>
                              </div>
                            );
                          default:
                            return (
                              <DocumentField
                                key={column.path}
                                label={column.label}
                                path={`${path}.${index}.${column.path}`}
                                isEditing={true}
                                schema={schema?.items?.properties?.[column.path]}
                              />
                            );
                        }
                      }

                      return (
                        <DocumentField
                          key={column.path}
                          label={column.label}
                          path={`${path}.${index}.${column.path}`}
                          isEditing={true}
                          schema={schema?.items?.properties?.[column.path]}
                        />
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          // View mode - show formatted values
          <div className="space-y-3">
            {fields.map((item, index) => (
              <div key={index}>
                {columns.map((column) => (
                  <div key={column.path}>
                    {column.label}: {formatValue(item, column)}
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Helper function to create an empty item based on schema
function createEmptyItem(schema: any) {
  if (!schema) return {};

  const result: any = {};

  if (schema.type === "object" && schema.properties) {
    Object.keys(schema.properties).forEach((key) => {
      const propSchema = schema.properties[key];

      if (propSchema.type === "string") {
        result[key] = "";
      } else if (propSchema.type === "number" || propSchema.type === "integer") {
        result[key] = null;
      } else if (propSchema.type === "boolean") {
        result[key] = false;
      } else if (propSchema.type === "array") {
        result[key] = [];
      } else if (propSchema.type === "object") {
        result[key] = createEmptyItem(propSchema);
      } else {
        result[key] = null;
      }
    });
  }

  return result;
}